import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, Paper, Button, Avatar, Card, CardContent } from '@mui/material'
import { AccessTime, Flag, Stars } from '@mui/icons-material'
import { useSearch } from '../hooks/useSearch.js'
import { loadTftAssets } from '../utils/tftAssets.js'
import { DDRAGON_BASE_URL } from '../config.js'
import TftRankSnapshot, { pickPrimaryLeagueEntry } from './TftRankSnapshot.js'
import {
  TFT_ICON_OVERRIDES,
  TFT_DISPLAY_NAME_OVERRIDES,
  TFT_TRAIT_NAME_OVERRIDES,
  TFT_ITEM_NAME_OVERRIDES
} from '../utils/tftMappings.js'
import './tft.css'

const formatDateTime = (timestamp) => {
  if (!timestamp) return 'Unknown'
  const date = new Date(timestamp)
  const datePart = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const timePart = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  return `${datePart} • ${timePart}`
}

const formatLastRound = (lastRound) => {
  if (!lastRound) return 'N/A'
  if (lastRound <= 4) return `1-${lastRound}`
  const adjusted = lastRound - 4
  const stageOffset = Math.floor((adjusted - 1) / 7)
  const stage = 2 + stageOffset
  const round = ((adjusted - 1) % 7) + 1
  return `${stage}-${round}`
}

const formatPlacement = (placement) => {
  if (!placement) return 'N/A'
  const suffix = placement === 1 ? 'ST' : placement === 2 ? 'ND' : placement === 3 ? 'RD' : 'TH'
  return `${placement}${suffix} PLACE`
}

const getPlacementClass = (placement) => {
  if (placement === 1) return 'tft-card--gold'
  if (placement === 2) return 'tft-card--silver'
  if (placement === 3) return 'tft-card--bronze'
  return 'tft-card--default'
}

const formatTraitName = (name) => {
  if (!name) return ''
  if (TFT_TRAIT_NAME_OVERRIDES[name]) return TFT_TRAIT_NAME_OVERRIDES[name]
  return name.replace(/^TFT\d+_/, '').replace('Unique', '')
}

const getTraitTierClass = (trait) => {
  if (!trait) return 'tft-trait--default'
  if (trait.tier_current >= 3) return 'tft-trait--gold'
  if (trait.tier_total === 1) return 'tft-trait--unique'
  if (trait.tier_current === 2) return 'tft-trait--silver'
  if (trait.tier_current === 1) return 'tft-trait--bronze'
  return 'tft-trait--default'
}

const getTraitPriority = (trait) => {
  const tierClass = getTraitTierClass(trait)
  const order = {
    'tft-trait--gold': 0,
    'tft-trait--unique': 1,
    'tft-trait--silver': 2,
    'tft-trait--bronze': 3
  }
  return order[tierClass] ?? 4
}

const getTftIconUrl = (characterId) => {
  if (!characterId) return ''
  const rawName = characterId.replace(/^TFT\d+_/i, '')
  const fileName = TFT_ICON_OVERRIDES[rawName] || rawName
  return `/photos/championImages/Icons/${encodeURIComponent(fileName)}.png`
}

const getTftDisplayName = (characterId) => {
  if (!characterId) return ''
  const rawName = characterId.replace(/^TFT\d+_/i, '')
  const normalized = rawName.toLowerCase()
  if (TFT_DISPLAY_NAME_OVERRIDES[normalized]) {
    return TFT_DISPLAY_NAME_OVERRIDES[normalized]
  }
  return normalized
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => `${word[0]?.toUpperCase() || ''}${word.slice(1)}`)
    .join(' ')
}

const getTftItemUrl = (itemName) => {
  if (!itemName) return ''
  return `/photos/items/tft-item/${encodeURIComponent(itemName)}.png`
}

const formatTftItemName = (itemName) => {
  if (!itemName) return ''
  if (TFT_ITEM_NAME_OVERRIDES[itemName]) return TFT_ITEM_NAME_OVERRIDES[itemName]
  const cleaned = itemName
    .replace(/^TFT\d+_/, '')
    .replace(/^TFT_Item_Artifact_/, '')
    .replace(/^TFT_Item_/, '')
    .replace(/^TFT_Assist_/, '')
    .replace(/^TFT\d+_Item_/, '')
    .replace(/^TFT\d+_Consumable_/, '')
  return cleaned
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim()
}

const TftPage = ({ data }) => {
  const { state, actions } = useSearch()
  const { tftCount, isLoading, userData } = state
  const { setTftCount, handleSearch } = actions
  const [openUnitsMatchId, setOpenUnitsMatchId] = useState(null)

  const matches = data?.matches || []
  const primaryLeague = useMemo(
    () => pickPrimaryLeagueEntry(data?.leagueEntries || [], data?.queueType),
    [data?.leagueEntries, data?.queueType]
  )
  const [assets, setAssets] = useState({ championImages: {}, traitImages: {} })
  const [assetsError, setAssetsError] = useState('')
  const didMountRef = useRef(false)

  useEffect(() => {
    let isMounted = true
    loadTftAssets()
      .then((result) => {
        if (isMounted) setAssets(result)
      })
      .catch(() => {
        if (isMounted) setAssetsError('Failed to load TFT assets.')
      })
    return () => {
      isMounted = false
    }
  }, [])

  const resolvedMatches = useMemo(() => {
    return matches.map((match) => {
      const traits = Array.isArray(match.traits)
        ? match.traits
          .filter((trait) => trait.style > 0)
          .sort((a, b) => {
            const priorityDiff = getTraitPriority(a) - getTraitPriority(b)
            if (priorityDiff !== 0) return priorityDiff
            return (b.num_units || 0) - (a.num_units || 0)
          })
        : []
      const units = Array.isArray(match.units) ? match.units : []

      const sortedUnits = [...units].sort((a, b) => {
        const tierDiff = (b.tier || 0) - (a.tier || 0)
        if (tierDiff !== 0) return tierDiff
        const itemDiff = (b.itemNames?.length || 0) - (a.itemNames?.length || 0)
        if (itemDiff !== 0) return itemDiff
        return (b.rarity || 0) - (a.rarity || 0)
      })

      return {
        ...match,
        traits,
        units: sortedUnits
      }
    })
  }, [matches])

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
    }
  }, [])

  return (
    <div className="tft-page">
      {assetsError && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {assetsError}
        </Typography>
      )}

      <div className="tft-content-grid">
        {(userData?.participant || primaryLeague) && (
          <aside className="tft-sidebar-column">
            <Card className="tft-overview-card" elevation={0}>
              <CardContent className="tft-overview-card__content">
                <div className="tft-overview-eyebrow">Player Snapshot</div>
                {userData?.participant && (
                  <div className="identity-compact identity-compact--inline identity-compact--tft">
                    <Avatar
                      className="identity-compact__avatar"
                      sx={{ width: 56, height: 56 }}
                      src={`${DDRAGON_BASE_URL}/img/profileicon/${userData.participant.profileIconId}.png`}
                    />
                    <div className="identity-compact__meta">
                      <div className="identity-compact__name">
                        <span>{userData.participant.playerName}</span>
                        <span className="identity-compact__tag">#{userData.participant.hashTag}</span>
                      </div>
                      <div className="identity-compact__level">Level {userData.participant.summonerLevel}</div>
                    </div>
                  </div>
                )}
                {primaryLeague && (
                  <TftRankSnapshot leagueEntry={primaryLeague} className="tft-league-card--compact tft-league-card--overview" />
                )}
              </CardContent>
            </Card>
          </aside>
        )}

        <div className="tft-match-column">
          {resolvedMatches.map((match) => (
            <Paper key={match.matchId} className={`tft-card ${getPlacementClass(match.placement)}`}>
              <div className="tft-card-left">
                <div className="tft-placement">{formatPlacement(match.placement)}</div>
                <div className="tft-meta-row">
                  <span className="tft-badge">{match.queueType?.toUpperCase() || 'TFT'}</span>
                  <span className="tft-meta-item tft-meta-chip">
                    <AccessTime className="tft-meta-icon" fontSize="inherit" />
                    {Math.round((match.gameLength || 0) / 60)}m
                  </span>
                </div>
                <div className="tft-meta-row">
                  <span className="tft-meta-item">{formatDateTime(match.gameDatetime)}</span>
                </div>
                <div className="tft-meta-divider" />
                <div className="tft-meta-row tft-meta-row--grid">
                  <span className="tft-meta-item">
                    <Stars className="tft-meta-icon" fontSize="inherit" />
                    Level {match.level ?? 'N/A'}
                  </span>
                  <span className="tft-meta-item">
                    <Flag className="tft-meta-icon" fontSize="inherit" />
                    Last round {formatLastRound(match.lastRound)}
                  </span>
                </div>
              </div>
              <div className="tft-card-right">
                {match.units.length > 0 && (() => {
                  const maxUnits = 9
                  const visibleUnits = match.units.slice(0, maxUnits)
                  const hiddenCount = match.units.length - visibleUnits.length
                  const hiddenUnits = match.units.slice(maxUnits)
                  const isPopoverOpen = openUnitsMatchId === match.matchId
                  return (
                    <div className="tft-units">
                      {visibleUnits.map((unit) => {
                        const itemNames = Array.isArray(unit.itemNames) ? unit.itemNames : []
                        return (
                          <div key={unit.character_id} className="tft-unit">
                            <div className={`tft-stars tft-stars--top${unit.tier === 3 ? ' tft-stars--gold' : ''}`}>
                              {unit.tier ? '★'.repeat(unit.tier) : ''}
                            </div>
                            <img
                              className="tft-unit-avatar"
                              src={getTftIconUrl(unit.character_id)}
                              alt={getTftDisplayName(unit.character_id)}
                              loading="lazy"
                              onError={(event) => {
                                event.currentTarget.style.visibility = 'hidden'
                              }}
                            />
                            <span className="tft-unit-label">
                              {getTftDisplayName(unit.character_id)}
                            </span>
                            <div className="tft-unit-items">
                              {itemNames.slice(0, 3).map((itemName, itemIndex) => (
                                <div key={`${itemName}-${itemIndex}`} className="tft-unit-item-wrap">
                                  <img
                                    className="tft-unit-item"
                                    src={getTftItemUrl(itemName)}
                                    alt={formatTftItemName(itemName)}
                                    loading="lazy"
                                    onError={(event) => {
                                      event.currentTarget.style.visibility = 'hidden'
                                    }}
                                  />
                                  <span className="tft-unit-item-label">{formatTftItemName(itemName)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                      {hiddenCount > 0 && (
                        <div className="tft-unit tft-unit--more">
                          <button
                            type="button"
                            className="tft-unit-more"
                            aria-label={`${hiddenCount} more units`}
                            aria-expanded={isPopoverOpen}
                            aria-controls={`hidden-units-${match.matchId}`}
                            onClick={() => {
                              setOpenUnitsMatchId((prev) => (prev === match.matchId ? null : match.matchId))
                            }}
                          >
                            {`+${hiddenCount}`}
                          </button>
                          {isPopoverOpen && (
                            <div
                              id={`hidden-units-${match.matchId}`}
                              className="tft-units-popover"
                              role="dialog"
                              aria-label="Hidden units"
                            >
                              <div className="tft-units-popover-header">
                                <button
                                  type="button"
                                  className="tft-units-popover-close"
                                  onClick={() => setOpenUnitsMatchId(null)}
                                  aria-label="Close hidden units"
                                >
                                  ✕
                                </button>
                              </div>
                              <div className="tft-units-popover-grid">
                                {hiddenUnits.map((unit) => {
                                  const itemNames = Array.isArray(unit.itemNames) ? unit.itemNames : []
                                  return (
                                    <div key={unit.character_id} className="tft-unit">
                                      <div className={`tft-stars tft-stars--top${unit.tier === 3 ? ' tft-stars--gold' : ''}`}>
                                        {unit.tier ? '★'.repeat(unit.tier) : ''}
                                      </div>
                                      <img
                                        className="tft-unit-avatar"
                                        src={getTftIconUrl(unit.character_id)}
                                        alt={getTftDisplayName(unit.character_id)}
                                        loading="lazy"
                                        onError={(event) => {
                                          event.currentTarget.style.visibility = 'hidden'
                                        }}
                                      />
                                      <span className="tft-unit-label">
                                        {getTftDisplayName(unit.character_id)}
                                      </span>
                                      <div className="tft-unit-items">
                                        {itemNames.slice(0, 3).map((itemName, itemIndex) => (
                                          <div key={`${itemName}-${itemIndex}`} className="tft-unit-item-wrap">
                                            <img
                                              className="tft-unit-item"
                                              src={getTftItemUrl(itemName)}
                                              alt={formatTftItemName(itemName)}
                                              loading="lazy"
                                              onError={(event) => {
                                                event.currentTarget.style.visibility = 'hidden'
                                              }}
                                            />
                                            <span className="tft-unit-item-label">{formatTftItemName(itemName)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })()}
                {match.traits.length > 0 && (
                  <div className="tft-traits">
                    {match.traits.map((trait) => (
                      <div
                        key={trait.name}
                        className={`tft-trait ${getTraitTierClass(trait)}`}
                        data-label={formatTraitName(trait.name)}
                        aria-label={formatTraitName(trait.name)}
                      >
                        <span
                          className="tft-trait-avatar tft-trait-avatar--muted"
                          style={{
                            backgroundImage: assets.traitImages[trait.name]?.icon
                              ? `url(${assets.traitImages[trait.name].icon})`
                              : assets.traitImages[trait.name]?.sprite
                                ? `url(${assets.traitImages[trait.name].sprite})`
                                : undefined,
                            backgroundPosition: assets.traitImages[trait.name]?.icon
                              ? 'center'
                              : assets.traitImages[trait.name]
                                ? `-${assets.traitImages[trait.name].x}px -${assets.traitImages[trait.name].y}px`
                                : undefined,
                            backgroundSize: assets.traitImages[trait.name]?.icon ? 'cover' : 'auto',
                            width: assets.traitImages[trait.name]?.icon
                              ? 20
                              : assets.traitImages[trait.name]?.w || 22,
                            height: assets.traitImages[trait.name]?.icon
                              ? 20
                              : assets.traitImages[trait.name]?.h || 22
                          }}
                        />
                        <span className="tft-trait-label">
                          {formatTraitName(trait.name)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Paper>
          ))}
          {data?.matches?.length > 0 && (
            <Box className="tft-load-more">
              <Button
                variant="outlined"
                disabled={isLoading}
                onClick={() => {
                  const nextCount = tftCount + 10
                  setTftCount(nextCount)
                  handleSearch(false, { tftCountOverride: nextCount })
                }}
              >
                {isLoading ? 'Loading...' : 'Show More'}
              </Button>
            </Box>
          )}
        </div>
      </div>
    </div>
  )
}

TftPage.propTypes = {
  data: PropTypes.object.isRequired
}

export default TftPage
